import { PropsWithChildren, ReactNode, useCallback, useMemo, useState } from "react";
import { IconName } from "lucide-react/dynamic";
import classnames from "classnames";
import { Tooltip } from "@ark-ui/react";

import { Icon as IconType } from "@/lib/api";
import Label from "@/components/label/label";
import IconAvatar from "@/components/icon-avatar/icon-avatar";
import Button from "@/components/button/button";
import useLayoutStore from "@/store/useLayoutStore";

import styles from "./navigation.module.css";

interface NavigationItemProps {
  icon?: IconName | IconType;
  active?: boolean;
  tooltip?: ReactNode;
  /** Replaces the icon with a colored dot, e.g. the highest warning level on the page. */
  dot?: { color: string; label: string };
}

export default function NavigationItem({
  children,
  icon,
  active,
  tooltip = children,
  dot,
}: PropsWithChildren<NavigationItemProps>) {
  const [{ isMenuCollapsed, isFullscreen }] = useLayoutStore((s) => s.context);
  const isCollapsed = useMemo(() => {
    return isFullscreen || isMenuCollapsed;
  }, [isFullscreen, isMenuCollapsed]);

  const [contentFocused, setContentFocused] = useState(false);
  const updateContentFocused = useCallback(
    (state: boolean) => () => {
      setContentFocused(state);
    },
    [setContentFocused],
  );

  const avatar = dot ? (
    <span className={styles.navigationDotSlot}>
      <span
        className={styles.navigationDot}
        style={{ backgroundColor: dot.color }}
        role="img"
        aria-label={dot.label}
      />
    </span>
  ) : (
    <IconAvatar icon={icon ?? "file"} />
  );

  return (
    <div
      className={classnames(styles.navigationItemWrapper, {
        [styles.navigationItemWrapperActive]: active,
      })}
      onFocus={updateContentFocused(true)}
      onBlur={updateContentFocused(false)}
    >
      <Tooltip.Root
        positioning={{
          placement: "right-start",
          offset: {
            mainAxis: 16,
          },
          strategy: "fixed",
        }}
        closeDelay={200}
        openDelay={0}
        unmountOnExit
        open={contentFocused || undefined}
      >
        <div className={styles.navigationItem}>
          {!isCollapsed ? (
            avatar
          ) : (
            <>
              <Tooltip.Trigger asChild>
                {(icon || dot) && <Button variant="ghost">{avatar}</Button>}
              </Tooltip.Trigger>
              <Tooltip.Positioner
                onMouseEnter={updateContentFocused(true)}
                onMouseLeave={updateContentFocused(false)}
              >
                <Tooltip.Content asChild>
                  {tooltip === children ? <Label>{tooltip}</Label> : tooltip}
                </Tooltip.Content>
              </Tooltip.Positioner>
            </>
          )}

          <div className={styles.navigationLabel}>{children}</div>
        </div>
      </Tooltip.Root>
    </div>
  );
}
